import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  Subscription,
  switchMap,
  throwError
} from 'rxjs';
import {CustomerApiModel} from '../model/customer-api.model';
import {environment} from '../../environment';

/**
 * @class
 * @description This class provides methods to interact with the Customer API.
 * @export
 * @name CustomerService
 * @service
 */
@Injectable({

  // We'll want this service to be provided as a singleton.
  providedIn: 'root'
})
export class CustomerService {

  /**
   * @constructor
   * @description This method instantiates a new instance of our PEO service provider with injected dependencies.
   * @memberof CustomerService
   * @name CustomerService#constructor()
   * @param {HttpClient} httpClient The injected instance of our HTTP Client service provider.
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our HTTP Client service provider.
     * @memberof CustomerService
     * @name CustomerService#httpClient
     * @private
     * @readonly
     * @type {HttpClient}
     */
    private readonly httpClient: HttpClient,
  ) {
  }

  /**
   * @async
   * @description This method asynchronously lists the Customers from the .NET API.
   * @memberof CustomerService
   * @method
   * @name CustomerService#list()
   * @public
   * @returns {Observable<CustomerApiModel[]>} An observable containing the response from the .NET Backend API.
   */
  public list(): Observable<CustomerApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<CustomerApiModel[]>(environment.normalizedApiUrl('/api/administrator/customer'), {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }

  /**
   * @async
   * @description This method asynchronously loads a Customer in the .NET API.
   * @memberof CustomerService
   * @method
   * @name CustomerService#list()
   * @param {number} customerId The customer identifier to load.
   * @public
   * @returns {Observable<CustomerApiModel | null>} An observable containing the response from the .NET Backend API.
   */
  public load(customerId: number): Observable<CustomerApiModel | null> {

    // Ensure the customer identifier is not empty.
    if (!customerId) return of(null);

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<CustomerApiModel>(environment.normalizedApiUrl('/api/administrator/customer/:customerId',
      {customerId}), {

      // Set the with credentials flag into the request options.
      withCredentials: true
    }).pipe(catchError((e: HttpErrorResponse): Observable<null> => e.status === 404
      ? of(null)
      : throwError((): HttpErrorResponse => e)));
  }

  /**
   * @async
   * @description This method asynchronously loads many customers from the .NET API.
   * @memberof CustomerService
   * @method
   * @name CustomerService#loadMany()
   * @param {number[]} identifiers The list of customer identifiers to load.
   * @public
   * @returns {Observable<CustomerApiModel[]>} An observable containing the response from the .NET Backend API.
   */
  public loadMany(identifiers: number[]): Observable<CustomerApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<CustomerApiModel[]>(environment.normalizedApiUrl('/api/administrator/customer/load'),
      identifiers, {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously loads many customers from the .NET API.
   * @memberof CustomerService
   * @method
   * @name CustomerService#loadManyMap()
   * @param {number[]} identifiers The list of customer identifiers to load.
   * @public
   * @returns {{[key: number]: Observable<CustomerApiModel}>} An observable containing the mapped response from the .NET Backend API.
   */
  public loadManyMap(identifiers: number[]): Observable<{[key: number]: CustomerApiModel}> {

    // Load the list of customers.
    return this.loadMany(identifiers).pipe(switchMap((customers: CustomerApiModel[]): Observable<{
      [key: number]: CustomerApiModel
    }> => {

      // Define our customer map.
      const customerMap: { [key: number]: CustomerApiModel } = {};

      // Iterate over each customer and add it to the map.
      customers.forEach(customer => customerMap[customer.id] = customer);

      // Return the observable containing the customer map.
      return of(customerMap);
    }));
  }

  /**
   * @async
   * @description This method asynchronously maps the Customers from the .NET API then maps them for use in a view.
   * @memberof CustomerService
   * @method
   * @name CustomerService#map()
   * @public
   * @returns {Observable<{[key: number]: CustomerApiModel}>} An observable containing the response from the .NET Backend API.
   */
  public map(): Observable<{ [key: number]: CustomerApiModel }> {

    // Load the list of customers.
    return this.list().pipe(switchMap((customers: CustomerApiModel[]): Observable<{
      [key: number]: CustomerApiModel
    }> => {

      // Define our customer map.
      const customerMap: { [key: number]: CustomerApiModel } = {};

      // Iterate over each customer and add it to the map.
      customers.forEach(customer => customerMap[customer.id] = customer);

      // Return the observable containing the customer map.
      return of(customerMap);
    }));
  }

  /**
   * @async
   * @description This method asynchronously searches the Customers in the .NET API.
   * @memberof CustomerService
   * @method
   * @name CustomerService#list()
   * @public
   * @returns {Observable<CustomerApiModel[]>} An observable containing the response from the .NET Backend API.
   */
  public search(term: string): Observable<CustomerApiModel[]> {

    // Ensure the term is not empty.
    if (!term?.trim()) return of([]);

    // Trim the term.
    term = term.trim();

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<CustomerApiModel[]>(environment.normalizedApiUrl('/api/administrator/customer/search/:term',
      {term}), {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }
}
